// The state an object list page keeps besides its rows: what a view opens
// with, the filter fields the library's builder is given, the Kanban's
// picklist fields, and the per-browser display preferences remembered in
// localStorage. Views themselves live on the server (listViewsApi.ts). Pure,
// so it can be unit tested (listState.test.ts).

import {
  type ColumnDef, type FilterFieldDef, type FilterRow, type KanbanFieldDef, labelFromKey, type ListDisplayMode, type MatrixData, type MatrixFigure,
  type MatrixMeasure, type MatrixSettings, type SortState,
} from '@olytic/foundation';

import { formatDate, formatDateTime, formatMoney, formatNumber, formatPercent, humanize } from './format';
import type { StoredView } from './listViewsApi';
import type { ObjectKey } from '../navigation';
import type { ObjectDef, ObjectFilterField } from './objectDefs';
import type { MetaFieldType, ObjectMeta, ObjectRow } from './objectsApi';
import { recordPath } from './routes';

/** Everything a view decides; the page is "modified" when it differs from what the view opened with. */
export interface ViewState {
  sort: SortState;
  filterRows: FilterRow[];
  filterLogic: string;
  columns: string[];
}

export const PAGE_SIZES = [25, 50, 100, 200] as const;
export const DEFAULT_PAGE_SIZE = 50;

/** The columns an object shows before anyone picks: those not marked hidden. */
export function defaultColumns(def: ObjectDef): string[] {
  return def.columns.filter((column) => !column.hidden).map((column) => column.key);
}

/** A view with no sort of its own (Recently viewed): the server's order. */
export const NO_SORT: SortState = { key: '', direction: 'desc' };

/** What a view opens with; a view that names no columns shows the object's default ones. */
export function viewState(view: Pick<StoredView, 'sort' | 'filters' | 'logic' | 'columns'>, columns: string[]): ViewState {
  return {
    sort: view.sort ?? NO_SORT,
    filterRows: view.filters,
    filterLogic: view.logic,
    columns: view.columns ?? columns,
  };
}

/**
 * The view state without fields the table no longer has: a removed field
 * drops out of a view quietly. Only once every field is known (`known` null
 * until /meta answers), so a field /meta adds is not dropped early.
 */
export function withoutRemovedFields(state: ViewState, known: { columns: ReadonlySet<string>; fields: ReadonlySet<string> } | null): ViewState {
  if (known === null) return state;
  const columns = state.columns.filter((key) => known.columns.has(key));
  const filterRows = state.filterRows.filter((row) => known.fields.has(row.field));
  const sort = state.sort.key === '' || known.columns.has(state.sort.key) || known.fields.has(state.sort.key) ? state.sort : NO_SORT;
  if (columns.length === state.columns.length && filterRows.length === state.filterRows.length && sort === state.sort) return state;
  // Logic numbers the rows, so it no longer fits once one is gone.
  return { sort, columns, filterRows, filterLogic: filterRows.length === state.filterRows.length ? state.filterLogic : '' };
}

type SortableColumn = Pick<ColumnDef<ObjectRow>, 'key' | 'sortable'>;

/**
 * The key `sort=` sends, and a view stores, for a sort on `key`: a column
 * whose `sortable` is a string sorts by that field (a contact's or lead's
 * Name by `last_name`), since the server sorts by fields, not by columns.
 * Any other key is itself.
 */
export function serverSortKey(columns: readonly SortableColumn[], key: string): string {
  const sortable = columns.find((column) => column.key === key)?.sortable;
  return typeof sortable === 'string' ? sortable : key;
}

/**
 * The header that shows a sort on `key`, among the columns the table
 * `displayed`: one that sorts by `key`, itself or through its string
 * `sortable` (Name for `last_name`). A hidden column never takes the sort,
 * since its header is not there to show it or turn it (the chooser lists a
 * contact's last_name, hidden, beside Name). When two displayed headers sort
 * by `key`, the one last `clicked` keeps it, so a second click on it turns
 * the sort, else the column that is itself `key`.
 */
export function sortColumnKey(displayed: readonly SortableColumn[], key: string, clicked = ''): string {
  if (key === '') return key;
  const sorting = displayed.filter((column) => serverSortKey([column], column.key) === key);
  return (sorting.find((c) => c.key === clicked) ?? sorting.find((c) => c.key === key) ?? sorting[0])?.key ?? key;
}

export function sameViewState(a: ViewState, b: ViewState): boolean {
  return (
    a.sort.key === b.sort.key &&
    a.sort.direction === b.sort.direction &&
    a.filterLogic.trim() === b.filterLogic.trim() &&
    JSON.stringify(a.filterRows.map(({ field, operator, value }) => [field, operator, value])) ===
      JSON.stringify(b.filterRows.map(({ field, operator, value }) => [field, operator, value])) &&
    a.columns.join('\u0000') === b.columns.join('\u0000')
  );
}

/** Picklist values: the definition's fixed ones, else what /meta read from the data. */
function optionsFor(def: ObjectDef, key: string, meta: ObjectMeta | null): string[] {
  const field = def.filterFields.find((f) => f.key === key);
  if (field?.options) return field.options;
  return meta?.fields.find((f) => f.key === key)?.options ?? [];
}

const YES_NO = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

/**
 * The library FilterBuilder's fields. A picklist whose values are not known
 * (no fixed values and /meta has not answered) is offered as text, so it can
 * still be filtered. Dates accept relative values ("last 30 days"), which
 * toApiFilters resolves when the query runs.
 */
export function builderFields(def: ObjectDef, meta: ObjectMeta | null, labels: Labels = NO_LABELS): FilterFieldDef[] {
  const curated = def.filterFields.map((field): FilterFieldDef => {
    switch (field.kind) {
      case 'select': {
        const options = optionsFor(def, field.key, meta);
        return options.length > 0
          ? { key: field.key, label: field.label, kind: 'select', options: options.map((v) => ({ value: v, label: humanize(v) })) }
          : { key: field.key, label: field.label, kind: 'text', operators: ['equals', 'not_equals'] };
      }
      case 'date':
        return { key: field.key, label: field.label, kind: 'date', relativeDates: true };
      case 'boolean':
        return { key: field.key, label: field.label, kind: 'boolean', options: YES_NO };
      default:
        return { key: field.key, label: field.label, kind: field.kind };
    }
  });
  // Every other field /meta lists: the table's columns the definition does not name.
  const named = new Set(def.filterFields.map((f) => f.key));
  const others = (meta?.fields ?? [])
    .filter((f) => !named.has(f.key))
    .map((f): FilterFieldDef => {
      const label = labelOf(f.key, labels);
      switch (f.type) {
        case 'number':
        case 'currency':
        case 'percent':
          return { key: f.key, label, kind: 'number' };
        case 'date':
        case 'datetime':
          return { key: f.key, label, kind: 'date', relativeDates: true };
        case 'boolean':
          return { key: f.key, label, kind: 'boolean', options: YES_NO };
        case 'picklist':
          return f.options && f.options.length > 0
            ? { key: f.key, label, kind: 'select', options: f.options.map((v) => ({ value: v, label: humanize(v) })) }
            : { key: f.key, label, kind: 'text', operators: ['equals', 'not_equals'] };
        default:
          return { key: f.key, label, kind: 'text' };
      }
    })
    .sort((a, b) => a.label.localeCompare(b.label));
  return [...curated, ...others];
}

/** Field labels by API key, from the object's record page; a key without one reads as a Title Case label. */
export type Labels = ReadonlyMap<string, string>;
const NO_LABELS: Labels = new Map();

export function labelOf(key: string, labels: Labels): string {
  return labels.get(key) ?? labelFromKey(key);
}

const yesNo = (value: unknown): string => (value === true ? 'Yes' : value === false ? 'No' : '');

/** How a column of each type shows its value. */
const FORMATS: Partial<Record<MetaFieldType, (value: unknown) => string>> = {
  number: formatNumber,
  currency: formatMoney,
  percent: formatPercent,
  date: formatDate,
  datetime: formatDateTime,
  boolean: yesNo,
  picklist: humanize,
};

/**
 * The columns /meta lists that the definition does not: hidden until picked
 * in the column chooser, sortable, and shown by their type. A lookup's name
 * (`owner_name` beside an `owner_id` lookup) links through its id, so it
 * edits as the lookup. A record-only column (long text) is no list column.
 */
export function tableColumns(def: ObjectDef, meta: ObjectMeta | null, labels: Labels = NO_LABELS): ColumnDef<ObjectRow>[] {
  const defined = new Set(def.columns.map((c) => c.key));
  const lookups = new Map(def.fields.flatMap((f) => (f.references ? [[f.name.replace(/_id$/, '_name'), { object: f.references, idKey: f.name }] as const] : [])));
  return (meta?.columns ?? [])
    .filter((c) => !defined.has(c.key) && !c.recordOnly)
    .map((c): ColumnDef<ObjectRow> => {
      const format = FORMATS[c.type];
      const numeric = c.type === 'number' || c.type === 'currency' || c.type === 'percent';
      return {
        key: c.key,
        label: labelOf(c.key, labels),
        sortable: true,
        hidden: true,
        // A list (participants, tags) reads as one comma-separated string.
        get: (row) => { const v = row[c.key]; return Array.isArray(v) ? v.join(', ') : (v ?? ''); },
        ...(format ? { render: (row: ObjectRow) => format(row[c.key]) } : {}),
        ...(numeric ? { figures: true, nowrap: true } : {}),
        ...(c.type === 'date' || c.type === 'datetime' ? { nowrap: true } : {}),
        // A lookup shows the related record's name and links to it (so it edits as the lookup).
        ...(c.lookup
          ? { link: { object: c.lookup.object as ObjectKey, idKey: c.lookup.idKey } }
          : lookups.has(c.key) ? { link: lookups.get(c.key)! } : {}),
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
}

const NUMERIC_TYPES = new Set<MetaFieldType>(['number', 'currency', 'percent']);
const FILTER_KINDS: Record<MetaFieldType, ObjectFilterField['kind']> = {
  text: 'text', number: 'number', currency: 'number', percent: 'number',
  date: 'date', datetime: 'date', boolean: 'boolean', picklist: 'select',
};

/**
 * An object described by the schema (its definition's `schema`) as a whole
 * definition: each column it names, labelled from the record page and shown
 * by its type in /meta (a lookup linked to its record), and each filter it
 * names, of its type. What the definition says about a column (width, hidden,
 * a format) goes over what the type says. Until /meta answers, the columns
 * show their values as they come. Any other definition is returned as it is.
 */
export function resolveDef(def: ObjectDef, meta: ObjectMeta | null, recordLabels: Labels = NO_LABELS): ObjectDef {
  const schema = def.schema;
  if (!schema) return def;
  // The list's own labels, over the record page's.
  const labels: Labels = schema.labels ? new Map([...recordLabels, ...Object.entries(schema.labels)]) : recordLabels;
  const columnOf = (key: string) => meta?.columns?.find((c) => c.key === key);
  const typeOf = (key: string) => columnOf(key)?.type ?? meta?.fields.find((f) => f.key === key)?.type;
  const columns = schema.columns.map(({ key, format: own, ...extra }): ColumnDef<ObjectRow> => {
    const type = typeOf(key);
    const format = own === 'raw' ? undefined : own ?? (type ? FORMATS[type] : undefined);
    const lookup = columnOf(key)?.lookup;
    return {
      key,
      label: labelOf(key, labels),
      sortable: true,
      // A name column keeps the library's link styling only without a custom render, so it formats through `get`.
      get: format && extra.primary ? (row) => format(row[key]) : (row) => row[key] ?? '',
      ...(format && !extra.primary ? { render: (row: ObjectRow) => format(row[key]) } : {}),
      ...(type && NUMERIC_TYPES.has(type) ? { figures: true, nowrap: true } : {}),
      ...(type === 'date' || type === 'datetime' ? { nowrap: true } : {}),
      ...(lookup ? { link: { object: lookup.object as ObjectKey, idKey: lookup.idKey } } : {}),
      ...(extra.primary ? { href: (row: ObjectRow) => recordPath(def.key, row.id) } : {}),
      ...extra,
    };
  });
  const filterFields = schema.filters.map((key): ObjectFilterField => {
    const type = typeOf(key);
    return { key, label: schema.filterLabels?.[key] ?? labelOf(key, labels), kind: type ? FILTER_KINDS[type] : 'text' };
  });
  return { ...def, columns, filterFields };
}

/** The visible columns the list must ask for by name: the table's extra ones. */
export function extraColumnKeys(visible: readonly string[], meta: ObjectMeta | null): string[] {
  const extra = new Set((meta?.columns ?? []).filter((c) => c.extra).map((c) => c.key));
  return visible.filter((key) => extra.has(key));
}

/** Option tones for picklists whose values read as good or bad. */
const OPTION_TONES: Record<string, Record<string, KanbanFieldDef['options'][number]['tone']>> = {
  stage: { closed_won: 'success', closed_lost: 'error' },
};

/**
 * The fields a Kanban can group by: the object's picklists, each with its
 * values in order (the definition's fixed values, else what /meta read from
 * the data). A picklist whose values are not known yet is left out.
 */
export function kanbanFieldDefs(def: ObjectDef, meta: ObjectMeta | null, labels: Labels = NO_LABELS): KanbanFieldDef<ObjectRow>[] {
  const named = new Set(def.filterFields.map((f) => f.key));
  // The object's other picklists with known values group a board too, when every row carries them.
  const shown = new Set((meta?.columns ?? []).filter((c) => !c.extra && !c.recordOnly).map((c) => c.key));
  const others = (meta?.fields ?? [])
    .filter((f) => f.type === 'picklist' && !named.has(f.key) && shown.has(f.key) && (f.options?.length ?? 0) > 0)
    .map((f) => ({ key: f.key, label: labelOf(f.key, labels) }));
  return [...def.filterFields.filter((field) => field.kind === 'select'), ...others]
    .flatMap((field) => {
      const options = optionsFor(def, field.key, meta);
      if (options.length === 0) return [];
      const tones = OPTION_TONES[field.key] ?? {};
      return [{
        key: field.key,
        label: field.label,
        options: options.map((value) => ({ value, label: humanize(value), ...(tones[value] ? { tone: tones[value] } : {}) })),
        get: (row: ObjectRow) => {
          const value = row[field.key];
          return value === null || value === undefined ? null : String(value);
        },
      }];
    });
}

// ── Remembered preferences (per browser, per object) ──────────────────────

/** Display choices remembered in this browser. Views, their columns and the default view are on the server. */
export interface ListPrefs {
  pageSize?: number;
  /** Table or Kanban, as last chosen. */
  displayMode?: ListDisplayMode;
  /** The picklist the Kanban was last grouped by. */
  kanbanGroupBy?: string;
  /** Row height, as last chosen. */
  density?: 'comfortable' | 'compact';
  /** The matrix's rows, columns and figures, as last chosen. */
  matrix?: MatrixSettings;
}

export const prefsKey = (objectKey: string) => `ops.objects.${objectKey}.list`;

function isFilterRows(value: unknown): value is FilterRow[] {
  return (
    Array.isArray(value) &&
    value.every(
      (r) =>
        typeof r === 'object' && r !== null &&
        typeof r.field === 'string' && typeof r.operator === 'string' && typeof r.value === 'string',
    )
  );
}

/** Reads remembered preferences; anything missing, unreadable or malformed is left out. Views kept here before are ignored. */
export function loadPrefs(storage: Pick<Storage, 'getItem'> | undefined, objectKey: string): ListPrefs {
  try {
    const raw = storage?.getItem(prefsKey(objectKey));
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return {};
    const p = parsed as Record<string, unknown>;
    const prefs: ListPrefs = {};
    if (typeof p.pageSize === 'number' && (PAGE_SIZES as readonly number[]).includes(p.pageSize)) prefs.pageSize = p.pageSize;
    if (p.displayMode === 'table' || p.displayMode === 'kanban' || p.displayMode === 'matrix') prefs.displayMode = p.displayMode;
    if (typeof p.kanbanGroupBy === 'string') prefs.kanbanGroupBy = p.kanbanGroupBy;
    if (p.density === 'comfortable' || p.density === 'compact') prefs.density = p.density;
    const matrix = readMatrixSettings(p.matrix);
    if (matrix) prefs.matrix = matrix;
    return prefs;
  } catch {
    return {};
  }
}

/** Stores preferences; a browser that refuses storage just doesn't remember them. */
export function savePrefs(storage: Pick<Storage, 'setItem'> | undefined, objectKey: string, prefs: ListPrefs): void {
  try {
    storage?.setItem(prefsKey(objectKey), JSON.stringify(prefs));
  } catch {
    // Private windows and full or blocked storage: nothing to remember.
  }
}

/** localStorage, or undefined where reading it throws. */
export function browserStorage(): Storage | undefined {
  try {
    return typeof window === 'undefined' ? undefined : window.localStorage;
  } catch {
    return undefined;
  }
}

// ── Drill-in links ────────────────────────────────────────────────────────

/** What a link opens a list with: a view to start from, and filters in place of its own. */
export interface LinkedFilters {
  view: string | null;
  rows: FilterRow[];
  logic: string;
}

/** `?view=…&filters=<JSON rows>&logic=…`, from a chart's drill-in; null without filters or when they don't parse. */
export function readLinkFilters(params: URLSearchParams): LinkedFilters | null {
  const raw = params.get('filters');
  if (!raw) return null;
  try {
    const rows: unknown = JSON.parse(raw);
    if (!isFilterRows(rows) || rows.length === 0) return null;
    return { view: params.get('view'), rows, logic: params.get('logic') ?? '' };
  } catch {
    return null;
  }
}

/** A list's address that opens it on `view` with these filters: the reverse of readLinkFilters. */
export function linkToFilters(path: string, { view, rows, logic }: { view?: string; rows: FilterRow[]; logic?: string }): string {
  const params = new URLSearchParams();
  if (view) params.set('view', view);
  params.set('filters', JSON.stringify(rows));
  if (logic) params.set('logic', logic);
  return `${path}?${params.toString()}`;
}

// ── Matrix ────────────────────────────────────────────────────────────────

const AGGREGATES = new Set(['sum', 'max', 'min', 'avg']);

function readMatrixSettings(value: unknown): MatrixSettings | null {
  if (typeof value !== 'object' || value === null) return null;
  const v = value as Record<string, unknown>;
  if (typeof v.rowKey !== 'string' || typeof v.columnKey !== 'string' || !Array.isArray(v.figures)) return null;
  const figures = v.figures.flatMap((f): MatrixFigure[] => {
    if (typeof f !== 'object' || f === null) return [];
    const { kind, key } = f as Record<string, unknown>;
    if (kind === 'count') return [{ kind: 'count' }];
    return typeof kind === 'string' && AGGREGATES.has(kind) && typeof key === 'string'
      ? [{ kind: kind as 'sum' | 'max' | 'min' | 'avg', key }]
      : [];
  });
  return { rowKey: v.rowKey, columnKey: v.columnKey, figures };
}

/** The number fields a matrix can sum, max, min or average, A to Z. */
export function matrixMeasures(meta: ObjectMeta | null, labels: Labels = NO_LABELS): MatrixMeasure[] {
  return (meta?.fields ?? [])
    .filter((f) => f.type === 'number' || f.type === 'currency' || f.type === 'percent')
    .map((f): MatrixMeasure => {
      const format = FORMATS[f.type];
      return { key: f.key, label: labelOf(f.key, labels), ...(format ? { format } : {}) };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * The server's cells as the library's MatrixData: each axis in its field's
 * option order, then any other value the records hold, then No value.
 */
export function toMatrixData(cells: MatrixData['cells'], rowField: KanbanFieldDef<ObjectRow>, columnField: KanbanFieldDef<ObjectRow>): MatrixData {
  const seen = { row: new Set<string>(), column: new Set<string>(), rowNone: false, columnNone: false };
  for (const key of Object.keys(cells)) {
    const [r = '', c = ''] = key.split('|');
    if (r === '-') seen.rowNone = true;
    else if (r.startsWith('=')) seen.row.add(r.slice(1));
    if (c === '-') seen.columnNone = true;
    else if (c.startsWith('=')) seen.column.add(c.slice(1));
  }
  const axis = (field: KanbanFieldDef<ObjectRow>, values: Set<string>, none: boolean) => {
    const known = new Set(field.options.map((o) => o.value));
    return [
      ...field.options.map((o) => ({ value: o.value, label: o.label })),
      ...[...values].filter((v) => !known.has(v)).sort().map((v) => ({ value: v, label: humanize(v) })),
      ...(none ? [{ value: null, label: 'No value' }] : []),
    ];
  };
  return { rows: axis(rowField, seen.row, seen.rowNone), columns: axis(columnField, seen.column, seen.columnNone), cells };
}

/** The view's filters narrowed to one matrix cell: each field equal to its value, or empty. */
export function cellFilters(
  rows: FilterRow[], logic: string, cell: { field: string; value: string | null }[],
): { filterRows: FilterRow[]; filterLogic: string } {
  const added = cell.map(({ field, value }): FilterRow => (value === null
    ? { field, operator: 'is_empty', value: '' }
    : { field, operator: 'equals', value }));
  const numbers = added.map((_, i) => String(rows.length + i + 1)).join(' AND ');
  return {
    filterRows: [...rows, ...added],
    filterLogic: logic.trim() === '' ? '' : `(${logic.trim()}) AND ${numbers}`,
  };
}
